import { Check, Star } from 'lucide-react'
import Reveal from './Reveal'

interface Tier {
  name: string
  price: string
  blurb: string
  note: string
  features: string[]
  cta: string
  popular?: boolean
}

const TIERS: Tier[] = [
  {
    name: 'Starter',
    price: '$1,500',
    blurb: 'For small businesses that need a clean, professional site with the essentials done properly.',
    note: 'You own everything. No lock-in, no monthly fees.',
    features: [
      'Up to 5 pages (Home, About, Services, FAQ, Contact)',
      'Mobile-optimised, fast-loading design',
      'On-page SEO setup (titles, schema, sitemap)',
      'Contact form with email notifications',
      'Booking or enquiry link integration',
      '2 rounds of revisions',
      'Delivered in 5 to 7 business days',
    ],
    cta: 'Get started',
  },
  {
    name: 'Pro',
    price: '$2,000',
    blurb: 'For local service businesses that need more pages, stronger local SEO, and Google Business Profile support.',
    note: 'Most popular with local service businesses.',
    features: [
      'Up to 8 pages',
      'Everything in Starter, plus:',
      'Google Business Profile setup and optimisation',
      'Schema markup for local SEO',
      'Blog setup with 3 SEO-optimised posts',
      'Custom resource or portfolio page',
      '3 rounds of revisions',
      'Delivered in 10 business days',
    ],
    cta: 'Go Pro',
    popular: true,
  },
  {
    name: 'Growth',
    price: '$2,500',
    blurb: 'For established businesses that need service pages, location pages, launch content, and ongoing search foundations.',
    note: 'Everything included, no surprises.',
    features: [
      'Up to 12 pages',
      'Everything in Pro, plus:',
      'Multi-staff or multi-location pages',
      'Custom forms and lead capture',
      'Core Web Vitals optimisation',
      '5 SEO blog posts included',
      'Directory listing submissions',
      '3 rounds of revisions + 1-hour training call',
      'Delivered in 14 business days',
    ],
    cta: 'Start growing',
  },
]

const scrollToContact = () => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })

const Pricing = () => {
  return (
    <section id="pricing" className="relative bg-cream-deep/50 py-24 lg:py-32">
      <div className="mx-auto max-w-container px-5 lg:px-8">
        <Reveal className="max-w-2xl">
          <p className="eyebrow">Pricing</p>
          <h2 className="font-display mt-4 text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl">
            Fixed prices. No hourly surprises.
          </h2>
          <p className="mt-6 text-lg text-ink-soft">
            Every package is scoped upfront, so you know exactly what you are getting and what it costs.
            One-time build, and you own the lot.
          </p>
        </Reveal>

        {/* Testimonial */}
        <Reveal delay={0.05} className="mt-12">
          <figure className="max-w-3xl rounded-xl2 border border-[var(--line)] bg-paper p-7 shadow-soft">
            <div className="mb-3 flex gap-1 text-amber-500">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-4 w-4 fill-current" />
              ))}
            </div>
            <blockquote className="font-display text-xl font-medium leading-snug text-ink sm:text-2xl">
              "CrateWeb built my website in just 4 days. Professional, fast, and exactly what I needed.
              I have already had new client enquiries from it."
            </blockquote>
            <figcaption className="mt-4 text-sm text-ink-soft">
              <span className="font-semibold text-ink">Husna</span> · Melbourne small business owner
            </figcaption>
          </figure>
        </Reveal>

        {/* Tiers */}
        <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-3">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.07}>
              <div
                className={`flex h-full flex-col rounded-xl2 p-8 ${
                  t.popular
                    ? 'bg-ink text-cream shadow-lift ring-1 ring-ink'
                    : 'bg-paper text-ink shadow-soft ring-1 ring-[var(--line)]'
                }`}
              >
                {t.popular && (
                  <span className="mb-5 inline-flex w-fit items-center gap-1.5 rounded-full bg-gold px-3 py-1 text-[0.72rem] font-bold uppercase tracking-wider text-white">
                    <Star className="h-3 w-3 fill-current" /> Most popular
                  </span>
                )}
                <h3 className={`font-display text-2xl font-bold ${t.popular ? 'text-cream' : 'text-ink'}`}>{t.name}</h3>
                <p className={`mt-2 text-[0.92rem] ${t.popular ? 'text-cream/70' : 'text-ink-soft'}`}>{t.blurb}</p>

                <div className="mt-6 flex items-end gap-2">
                  <span className="font-display text-5xl font-extrabold tracking-tight">{t.price}</span>
                  <span className={`mb-1.5 text-sm ${t.popular ? 'text-cream/60' : 'text-ink-faint'}`}>one-time</span>
                </div>
                <p className={`mt-2 text-[0.82rem] font-medium ${t.popular ? 'text-gold-bright' : 'text-gold'}`}>{t.note}</p>

                <ul className="mt-7 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2.5 text-[0.9rem] ${t.popular ? 'text-cream/90' : 'text-ink-soft'}`}>
                      <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${t.popular ? 'text-gold-bright' : 'text-gold'}`} strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={scrollToContact}
                  className={`btn mt-8 w-full ${t.popular ? 'btn-gold' : 'btn-primary'}`}
                >
                  {t.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Notes */}
        <Reveal delay={0.1} className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-[var(--line)] bg-paper p-5 text-[0.92rem] text-ink-soft">
            <span className="font-semibold text-ink">Launching or rebranding?</span> Ask about the Business
            Launch Package, from $4,000, with website, profile, and listings done together.
          </div>
          <div className="rounded-2xl border border-[var(--line)] bg-paper p-5 text-[0.92rem] text-ink-soft">
            <span className="font-semibold text-ink">After launch,</span> keep growing with a retainer from
            $199/mo for hosting, updates, SEO content, and profile management.
          </div>
        </Reveal>
      </div>
    </section>
  )
}

export default Pricing
